import { Banner, Box, Button, Flex, Icon, Pane, Text } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { ObjectReferences } from '@fullstory/proto/fs/services/dx-objects';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { ReferencesTable } from './ReferencesTable';

interface ReferencesTabProps {
  objectReferences: ObjectReferences | null;
  name?: string;
  objectType: string;
  description?: string;
  issues?: IssuesProps;
}

interface IssuesProps {
  id: string;
  onArchive: (id: string) => void;
  onUpdate: () => void;
  watched: boolean;
  objectType: string;
  canModifyObject: boolean;
}

const IssuesBanner: React.FC<IssuesProps> = ({
  id,
  onArchive,
  onUpdate,
  watched,
  objectType,
  canModifyObject,
}) => (
  <Box data-component="IssuesBanner" paddingTop={16}>
    <Banner.Root variant="warning">
      <Banner.Pane>
        <Box display="flex" alignItems="center" gap={12} padding={[8, 16, 8, 16]} width={1}>
          <Box>
            <Icon name="warning" color="default" size={16} />
          </Box>
          <Box display="flex" flexDirection="column" flex={1}>
            <Text appearance="bodyMedium">
              <FormattedMessage
                defaultMessage="The {objectType} stopped generating event traffic in the last 72 hours."
                values={{ objectType: objectType.toLowerCase() }}
                id="35ZYye"
              />
            </Text>
          </Box>
          {canModifyObject && !watched ? (
            <Button
              data-component="ReferencesArchiveButton"
              testId="references-archive-button"
              variant="fill-destructive"
              onClick={() => onArchive(id)}
              size="x-small"
            >
              <FormattedMessage defaultMessage="Archive" id="hrgo+E" />
            </Button>
          ) : (
            <Button
              data-component="ReferencesUpdateButton"
              testId="references-update-button"
              variant="outline"
              onClick={onUpdate}
              size="x-small"
            >
              <FormattedMessage defaultMessage="Update" id="BWpuKl" />
            </Button>
          )}
        </Box>
      </Banner.Pane>
    </Banner.Root>
  </Box>
);

const EmptyState: React.FC<{ objectType: string }> = ({ objectType }) => (
  <Pane data-component="EmptyState" border="standard" borderRadius={4}>
    <Box padding={32}>
      <Flex column alignItems="center" gap={16}>
        <Text appearance="bodyMedium">
          <FormattedMessage
            defaultMessage="This {objectType} is not in use by anything in your library."
            values={{ objectType: objectType.toLowerCase() }}
            id="DGXrAL"
          />
        </Text>
        <Text appearance="bodySmall" color="secondary" align="center">
          <FormattedMessage
            defaultMessage="Your library includes all dashboards, segments, metrics, funnels, retention charts, journeys and heatmaps created by your organization."
            id="HhxQc3"
          />
        </Text>
      </Flex>
    </Box>
  </Pane>
);

const determineArticle = (objectType: string) =>
  ['element', 'event'].includes(objectType)
    ? intl.formatMessage({ defaultMessage: 'an', id: '+OLW5L' })
    : intl.formatMessage({ defaultMessage: 'a', id: 'H0D8kt' });

export const ReferencesTab: React.FC<ReferencesTabProps> = ({
  name,
  description,
  objectReferences,
  objectType,
  issues,
}) => (
  <Box data-component="ReferencesTab">
    <Box paddingBottom={24}>
      {name && (
        <Box paddingBottom={24}>
          <Text appearance="heading3">{name}</Text>
          {description && (
            <Box paddingTop={16}>
              <Text appearance="bodyMedium" color="secondary">
                {description}
              </Text>
            </Box>
          )}
        </Box>
      )}
      <Text appearance="heading3">
        <FormattedMessage defaultMessage="References" id="nCQY4D" />
      </Text>
      <Box marginTop={8} marginBottom={32}>
        <Text appearance="bodyMedium">
          <FormattedMessage
            defaultMessage="References show where {article} {objectType} is being used in your library. This does not include private objects."
            id="fOR5jm"
            values={{
              article: determineArticle(objectType.toLowerCase()),
              objectType: objectType.toLowerCase(),
            }}
          />
        </Text>
      </Box>
      {(!objectReferences || objectReferences.objectDetails.length === 0) && (
        <EmptyState objectType={objectType} />
      )}
      {objectReferences && objectReferences.objectDetails.length > 0 && (
        <ReferencesTable objectDetails={objectReferences.objectDetails} />
      )}
      {issues && <IssuesBanner {...issues} />}
    </Box>
  </Box>
);
